import { Area as AreaGenerator, Line as LineGenerator } from 'd3-shape';
import { kebabCase } from 'lodash';
import React, { memo } from 'react';
import { LineGraphArea } from './LineGraphArea';
import { LineGraphLine as Line } from './LineGraphLine';
import { LineGraphPoints } from './LineGraphPoints';
import { LineGraphTrendLine as TrendLine } from './LineGraphTrendLine';
import { LineGraphLine, LineGraphPoint, LineGraphTrendLine } from './useLines';

interface LineGraphLinesLayerProps {
    lines: LineGraphLine[];
    points: LineGraphPoint[];
    innerWidth: number;
    innerHeight: number;
    margin: {
        top: number;
        right: number;
        bottom: number;
        left: number;
    };
    trendLines?: LineGraphTrendLine[];
    areaGradientPrefix: string;
    enableArea?: boolean;
    enablePoints?: boolean;
    disableAnimation: boolean;
    hoveredLineId?: string | undefined;
    lineGenerator: LineGenerator<{
        x: number;
        y: number;
    }>;
    areaGenerator: AreaGenerator<{
        x: number;
        y: number;
    }>;
    onLineHover: (lineId: string) => void;
    onLineLeave: () => void;
    onMouseMove: (e: React.MouseEvent) => void;
}

/**
 *
 * @returns A custom SVG layer that manages individual line rendering, line hovering,
 * and the slice tooltip when hovering on a individual line
 */
export const LineGraphLinesLayer: React.FC<LineGraphLinesLayerProps> = memo(
    ({
        lines,
        points,
        innerWidth,
        innerHeight,
        margin,
        trendLines,
        areaGradientPrefix,
        enableArea,
        enablePoints,
        disableAnimation,
        hoveredLineId,
        lineGenerator,
        areaGenerator,
        onLineHover,
        onLineLeave,
        onMouseMove
    }) => {
        const linesReversed = lines.slice(0).reverse();

        return (
            <>
                <rect
                    x={margin.left}
                    y={margin.top}
                    width={innerWidth}
                    height={innerHeight}
                    fill='transparent'
                    onMouseEnter={onMouseMove}
                    onMouseMove={onMouseMove}
                />
                {enableArea &&
                    linesReversed.map(({ id, points: linePoints }) => (
                        <LineGraphArea
                            key={id}
                            lineId={id}
                            hoverId={hoveredLineId}
                            disableAnimation={disableAnimation}
                            fill={`url(#${areaGradientPrefix}${kebabCase(id)})`}
                            areaGenerator={areaGenerator}
                            points={linePoints.map((d) => d.position)}
                        />
                    ))}
                {linesReversed.map(({ id, points: linePoints, color, drilldownUrl }) => (
                    <Line
                        key={id}
                        lineId={id}
                        hoverId={hoveredLineId}
                        color={color}
                        disableAnimation={disableAnimation}
                        strokeWidth={2}
                        points={linePoints.map((d) => d.position)}
                        lineGenerator={lineGenerator}
                        onLineHover={onLineHover}
                        onLineLeave={onLineLeave}
                        onMouseMove={onMouseMove}
                        drilldownUrl={drilldownUrl}
                    />
                ))}
                {enablePoints && (
                    <LineGraphPoints points={points} hoverId={hoveredLineId} disableAnimation={disableAnimation} />
                )}
                {trendLines?.map((line) => (
                    <TrendLine
                        key={`trend-${line.id}`}
                        id={line.id}
                        hoverId={hoveredLineId}
                        width={innerWidth}
                        x1={line.x1}
                        x2={line.x2}
                        y1={line.y1}
                        y2={line.y2}
                        onLineHover={onLineHover}
                        onLineLeave={onLineLeave}
                        onMouseMove={onMouseMove}
                        opacity={1}
                        color={line.color}
                    />
                ))}
            </>
        );
    }
);
